import { addMinutes } from "date-fns";

import { haversineDistanceMeters } from "../../../../../utils/geo";
import {
  LockPosition,
  ProposedRoute,
  RouteColorName,
  RouteBuilderProperty,
  RouteBuilderSession,
  RouteEndpointLocation,
  RouteStop,
  UnroutedJob,
} from "../../../../../types/routeBuilder";
import { leastUsedRouteColor } from "../../../../../components/Maps/ActiveRoutes/routeColor";
import {
  createPropertyVisitId,
  createSessionId,
  incorporatesTime,
} from "./helpers";
import { withDefaultServiceTime } from "./generationProperties";
import { suggestRouteNamePrefix } from "./serviceHelpers";
import { clampToUnlockedRange } from "./lockUtils";

export const UNASSIGNED_ROUTE_ID = "__unassigned__";

type PropertyVisit = {
  propertyVisitId: string;
  property: RouteBuilderProperty;
  lock: LockPosition;
};

function rebuildStops(
  visits: PropertyVisit[],
  plannedDispatchTime: string,
  incorporateTime: boolean,
): RouteStop[] {
  let currentTime = plannedDispatchTime;
  return visits.map(({ propertyVisitId, property, lock }, index) => {
    const estimatedArrival = currentTime;
    currentTime = addMinutes(
      new Date(currentTime),
      Math.round(
        (incorporateTime ? property.estimatedServiceTimeSeconds : 0) / 60,
      ) + 5,
    ).toISOString();
    return {
      propertyVisitId,
      propertyId: property.jobId,
      order: index,
      estimatedArrival,
      estimatedDeparture: currentTime,
      lock,
    };
  });
}

function rebuildMetrics(
  properties: RouteBuilderProperty[],
  start: { lat: number; lng: number } | null,
  end: { lat: number; lng: number } | null,
  /** Keep last encoded path until re-optimize so the map can fade it, not hide it. */
  previousDrivingPath = "",
  /** With time off, service times do not count toward the route. */
  incorporateTime = true,
) {
  let distanceMeters = 0;
  let previous = start ?? properties[0]?.location ?? end;
  for (const property of properties) {
    if (previous) {
      distanceMeters += haversineDistanceMeters(previous, property.location);
    }
    previous = property.location;
  }
  if (properties.length > 0 && previous && end) {
    distanceMeters += haversineDistanceMeters(previous, end);
  }
  const serviceDurationSeconds = incorporateTime
    ? properties.reduce(
        (total, property) => total + property.estimatedServiceTimeSeconds,
        0,
      )
    : 0;
  const drivingDurationSeconds = properties.length * 5 * 60;
  return {
    drivingDurationSeconds,
    serviceDurationSeconds,
    totalDurationSeconds: drivingDurationSeconds + serviceDurationSeconds,
    drivingDistanceMeters: Math.round(distanceMeters),
    drivingPath: previousDrivingPath,
  };
}

function visitsForRoute(
  session: RouteBuilderSession,
  routeId: string,
): PropertyVisit[] {
  const route = session.routes.find((r) => r.id === routeId);
  if (!route) return [];
  return route.stops
    .map((stop) => {
      const property = session.availableProperties.find(
        (p) => p.jobId === stop.propertyId,
      );
      return property
        ? {
            propertyVisitId: stop.propertyVisitId,
            property,
            lock: stop.lock === undefined ? property.lock ?? null : stop.lock,
          }
        : null;
    })
    .filter((visit): visit is PropertyVisit => Boolean(visit));
}

function syncRouteFromProperties(
  session: RouteBuilderSession,
  routeId: string,
  visits: PropertyVisit[],
) {
  return {
    ...session,
    status: "adjusted" as const,
    routes: session.routes.map((route) => {
      if (route.id !== routeId) return route;
      return {
        ...route,
        stops: rebuildStops(
          visits,
          session.inputs.plannedDispatchTime,
          incorporatesTime(session.inputs),
        ),
        metrics: rebuildMetrics(
          visits.map((visit) => visit.property),
          route.startLocation.location,
          route.endLocation.location,
          route.metrics.drivingPath,
          incorporatesTime(session.inputs),
        ),
      };
    }),
  };
}

/** Move one property visit within or between routes, or to/from the pool. */
export function movePropertyVisit(
  session: RouteBuilderSession,
  fromRouteId: string,
  toRouteId: string,
  propertyVisitId: string,
  toIndex: number,
): RouteBuilderSession {
  if (
    toRouteId !== UNASSIGNED_ROUTE_ID &&
    !session.routes.some((route) => route.id === toRouteId)
  )
    return session;
  const sourceStop = session.routes
    .find((route) => route.id === fromRouteId)
    ?.stops.find((stop) => stop.propertyVisitId === propertyVisitId);
  const sourceUnrouted =
    fromRouteId === UNASSIGNED_ROUTE_ID
      ? session.unroutedJobs.find(
          (job) => job.propertyVisitId === propertyVisitId,
        )
      : undefined;
  if (!sourceStop && !sourceUnrouted) return session;
  const propertyId = sourceStop?.propertyId ?? sourceUnrouted?.jobId;
  const moving = session.availableProperties.find(
    (p) => p.jobId === propertyId,
  );
  if (!moving || !propertyId) return session;

  const movingLock = sourceStop?.lock ?? null;
  if (movingLock != null && toRouteId !== UNASSIGNED_ROUTE_ID) return session;

  const unroutedJobs = [...(session.unroutedJobs ?? [])];

  if (
    fromRouteId === UNASSIGNED_ROUTE_ID &&
    toRouteId === UNASSIGNED_ROUTE_ID
  ) {
    const fromIndex = unroutedJobs.findIndex(
      (job) => job.propertyVisitId === propertyVisitId,
    );
    const insertAt = Math.max(0, Math.min(toIndex, unroutedJobs.length - 1));
    if (fromIndex === insertAt) return session;
    const [visit] = unroutedJobs.splice(fromIndex, 1);
    unroutedJobs.splice(insertAt, 0, visit);
    return { ...session, status: "adjusted", unroutedJobs };
  }

  if (fromRouteId === UNASSIGNED_ROUTE_ID) {
    const nextUnrouted = unroutedJobs.filter(
      (job) => job.propertyVisitId !== propertyVisitId,
    );
    const toVisits = visitsForRoute(session, toRouteId);
    const insertAt = clampToUnlockedRange(toVisits, toIndex);
    const nextTo = [...toVisits];
    nextTo.splice(insertAt, 0, {
      propertyVisitId,
      property: moving,
      lock: null,
    });
    const next = syncRouteFromProperties(session, toRouteId, nextTo);
    return {
      ...next,
      unroutedJobs: nextUnrouted,
    };
  }

  if (toRouteId === UNASSIGNED_ROUTE_ID) {
    const fromVisits = visitsForRoute(session, fromRouteId).filter(
      (visit) => visit.propertyVisitId !== propertyVisitId,
    );
    const next = syncRouteFromProperties(session, fromRouteId, fromVisits);
    unroutedJobs.splice(
      Math.max(0, Math.min(toIndex, unroutedJobs.length)),
      0,
      unassignedJob(propertyVisitId, moving),
    );
    return {
      ...next,
      unroutedJobs,
    };
  }

  const fromVisits = visitsForRoute(session, fromRouteId).filter(
    (visit) => visit.propertyVisitId !== propertyVisitId,
  );

  if (fromRouteId === toRouteId) {
    const ordered = visitsForRoute(session, fromRouteId);
    const movingVisit = ordered.find(
      (visit) => visit.propertyVisitId === propertyVisitId,
    );
    if (!movingVisit) return session;
    const without = ordered.filter(
      (visit) => visit.propertyVisitId !== propertyVisitId,
    );
    const insertAt = clampToUnlockedRange(without, toIndex);
    without.splice(insertAt, 0, movingVisit);
    return syncRouteFromProperties(session, fromRouteId, without);
  }

  let next = syncRouteFromProperties(session, fromRouteId, fromVisits);
  const toVisits = visitsForRoute(next, toRouteId);
  const insertAt = clampToUnlockedRange(toVisits, toIndex);
  const nextTo = [...toVisits];
  nextTo.splice(insertAt, 0, { propertyVisitId, property: moving, lock: null });
  next = syncRouteFromProperties(next, toRouteId, nextTo);
  return next;
}

export function updatePropertyConstraints(
  session: RouteBuilderSession,
  propertyId: string,
  patch: Partial<
    Pick<
      RouteBuilderProperty,
      | "notes"
      | "estimatedServiceTimeSeconds"
      | "serviceTimeSource"
      | "dayOfWeek"
      | "serviceByDeadline"
    >
  >,
): RouteBuilderSession {
  const availableProperties = session.availableProperties.map((property) => {
    if (property.jobId !== propertyId) return property;
    const next = { ...property, ...patch };
    if (patch.estimatedServiceTimeSeconds != null && !patch.serviceTimeSource) {
      next.serviceTimeSource = "manual";
    }
    return next;
  });

  let updated: RouteBuilderSession = {
    ...session,
    status: "adjusted",
    availableProperties,
  };

  for (const route of session.routes) {
    if (!route.stops.some((stop) => stop.propertyId === propertyId)) continue;
    updated = syncRouteFromProperties(
      updated,
      route.id,
      visitsForRoute(updated, route.id),
    );
  }
  return updated;
}

export function setPropertyVisitLock(
  session: RouteBuilderSession,
  propertyVisitId: string,
  lock: LockPosition,
): RouteBuilderSession {
  const route = session.routes.find((candidate) =>
    candidate.stops.some((stop) => stop.propertyVisitId === propertyVisitId),
  );
  if (!route) return session;
  const visits = visitsForRoute(session, route.id).map((visit) =>
    visit.propertyVisitId === propertyVisitId ? { ...visit, lock } : visit,
  );
  const ordered = [
    ...visits.filter((visit) => visit.lock === "start"),
    ...visits.filter((visit) => visit.lock == null),
    ...visits.filter((visit) => visit.lock === "end"),
  ];
  return syncRouteFromProperties(session, route.id, ordered);
}

export function addPropertyVisit(
  session: RouteBuilderSession,
  routeId: string,
  property: RouteBuilderProperty,
  /** Where to insert; appends when omitted. */
  toIndex?: number,
): RouteBuilderSession {
  if (!session.routes.some((route) => route.id === routeId)) return session;
  const existingProperty = session.availableProperties.find(
    (candidate) => candidate.jobId === property.jobId,
  );
  const propertyForVisit = existingProperty ?? {
    ...withDefaultServiceTime(
      property,
      session.inputs.defaultServiceTimeMinutes,
    ),
    lock: null,
  };
  const availableProperties = existingProperty
    ? session.availableProperties
    : [...session.availableProperties, propertyForVisit];
  // On a route it's no longer unassigned, so drop any visit of it still
  // waiting there (a generated plan's leftover, say).
  const unroutedJobs = session.unroutedJobs.filter(
    (job) => job.jobId !== property.jobId,
  );
  const nextSession = { ...session, availableProperties, unroutedJobs };
  const visits = visitsForRoute(nextSession, routeId);
  const insertAt = clampToUnlockedRange(visits, toIndex ?? visits.length);
  visits.splice(insertAt, 0, {
    propertyVisitId: createPropertyVisitId(),
    property: propertyForVisit,
    lock: null,
  });
  return syncRouteFromProperties(nextSession, routeId, visits);
}

/** Where a saved session visit sits now. */
export type PropertyVisitLocation = {
  routeId: string;
  propertyId: string;
  lock: LockPosition;
};

export function locatePropertyVisit(
  session: RouteBuilderSession,
  propertyVisitId: string,
): PropertyVisitLocation | null {
  const job = session.unroutedJobs.find(
    (candidate) => candidate.propertyVisitId === propertyVisitId,
  );
  if (job) {
    return { routeId: UNASSIGNED_ROUTE_ID, propertyId: job.jobId, lock: null };
  }
  for (const route of session.routes) {
    const stop = route.stops.find(
      (candidate) => candidate.propertyVisitId === propertyVisitId,
    );
    if (stop) {
      return {
        routeId: route.id,
        propertyId: stop.propertyId,
        lock: stop.lock ?? null,
      };
    }
  }
  return null;
}

/**
 * Dirty routes after an edit: routes whose stops changed, or whose endpoints
 * changed while they have stops, need re-optimizing. Removed routes drop out.
 * Ops return the same route object for routes they did not touch.
 */
export function nextDirtyRouteIds(
  dirty: Set<string>,
  before: RouteBuilderSession,
  after: RouteBuilderSession,
): Set<string> {
  const previous = new Map(before.routes.map((route) => [route.id, route]));
  const next = new Set(
    Array.from(dirty).filter((id) =>
      after.routes.some((route) => route.id === id),
    ),
  );
  for (const route of after.routes) {
    const old = previous.get(route.id);
    if (!old) continue;
    const endpointsChanged =
      old.startLocation !== route.startLocation ||
      old.endLocation !== route.endLocation;
    if (
      old.stops !== route.stops ||
      (endpointsChanged && route.stops.length > 0)
    ) {
      next.add(route.id);
    }
  }
  return next;
}

export type PropertyPatch = Partial<
  Pick<
    RouteBuilderProperty,
    "notes" | "estimatedServiceTimeSeconds" | "dayOfWeek" | "serviceByDeadline"
  >
>;

/**
 * Move a property visit to the end of a route's unlocked stops, or to the
 * unassigned pool, then apply any property patch. Moving a locked stop clears
 * its lock. The patch still applies when the move is refused.
 */
export function assignPropertyVisitToRoute(
  session: RouteBuilderSession,
  propertyVisitId: string,
  toRouteId: string,
  { patch = {} }: { patch?: PropertyPatch } = {},
): RouteBuilderSession {
  const from = locatePropertyVisit(session, propertyVisitId);
  if (!from) return session;
  const moved = moveToRoute(session, propertyVisitId, from.routeId, toRouteId);
  return Object.keys(patch).length > 0
    ? updatePropertyConstraints(moved, from.propertyId, patch)
    : moved;
}

function moveToRoute(
  session: RouteBuilderSession,
  propertyVisitId: string,
  fromRouteId: string,
  toRouteId: string,
): RouteBuilderSession {
  if (fromRouteId === toRouteId) return session;
  if (toRouteId === UNASSIGNED_ROUTE_ID) {
    return unassignPropertyVisit(session, propertyVisitId);
  }
  if (!session.routes.some((route) => route.id === toRouteId)) return session;
  const pooled =
    fromRouteId === UNASSIGNED_ROUTE_ID
      ? session
      : unassignPropertyVisit(session, propertyVisitId);
  return movePropertyVisit(
    pooled,
    UNASSIGNED_ROUTE_ID,
    toRouteId,
    propertyVisitId,
    Number.MAX_SAFE_INTEGER,
  );
}

export function removePropertyVisit(
  session: RouteBuilderSession,
  propertyVisitId: string,
): RouteBuilderSession {
  const withoutUnassigned = removeUnassignedVisit(session, propertyVisitId);
  if (withoutUnassigned !== session) return withoutUnassigned;
  const route = session.routes.find((candidate) =>
    candidate.stops.some((stop) => stop.propertyVisitId === propertyVisitId),
  );
  if (!route) return session;
  return syncRouteFromProperties(
    session,
    route.id,
    visitsForRoute(session, route.id).filter(
      (visit) => visit.propertyVisitId !== propertyVisitId,
    ),
  );
}

export function removeUnassignedVisit(
  session: RouteBuilderSession,
  propertyVisitId: string,
): RouteBuilderSession {
  const unroutedJobs = session.unroutedJobs.filter(
    (job) => job.propertyVisitId !== propertyVisitId,
  );
  return unroutedJobs.length === session.unroutedJobs.length
    ? session
    : { ...session, status: "adjusted", unroutedJobs };
}

/** Adding creates waiting visits; existing route visits stay where the planner put them. */
export function addPropertiesToUnassigned(
  session: RouteBuilderSession,
  properties: RouteBuilderProperty[],
): RouteBuilderSession {
  const pooled = new Set(session.unroutedJobs.map((job) => job.jobId));
  const available = new Map(
    session.availableProperties.map((property) => [property.jobId, property]),
  );
  const additions: UnroutedJob[] = [];
  for (const property of properties) {
    if (pooled.has(property.jobId)) continue;
    pooled.add(property.jobId);
    const existing = available.get(property.jobId);
    const saved = {
      ...withDefaultServiceTime(
        property,
        session.inputs.defaultServiceTimeMinutes,
      ),
      ...existing,
      updatedAt: property.updatedAt ?? existing?.updatedAt,
      assignedRoutes: property.assignedRoutes ?? existing?.assignedRoutes,
      lock: existing?.lock ?? null,
    };
    available.set(property.jobId, saved);
    additions.push(unassignedJob(createPropertyVisitId(), saved));
  }
  if (additions.length === 0) return session;
  return {
    ...session,
    status: "adjusted",
    availableProperties: Array.from(available.values()),
    unroutedJobs: [...session.unroutedJobs, ...additions],
  };
}

/** Move a route stop into the unassigned pool, including a locked stop. */
export function unassignPropertyVisit(
  session: RouteBuilderSession,
  propertyVisitId: string,
): RouteBuilderSession {
  const route = session.routes.find((candidate) =>
    candidate.stops.some((stop) => stop.propertyVisitId === propertyVisitId),
  );
  const stop = route?.stops.find(
    (candidate) => candidate.propertyVisitId === propertyVisitId,
  );
  const property = session.availableProperties.find(
    (candidate) => candidate.jobId === stop?.propertyId,
  );
  if (!route || !stop || !property) return session;

  const next = syncRouteFromProperties(
    session,
    route.id,
    visitsForRoute(session, route.id).filter(
      (visit) => visit.propertyVisitId !== propertyVisitId,
    ),
  );
  return {
    ...next,
    unroutedJobs: [
      ...(session.unroutedJobs ?? []),
      unassignedJob(propertyVisitId, property),
    ],
  };
}

function unassignedJob(
  propertyVisitId: string,
  property: RouteBuilderProperty,
): UnroutedJob {
  return {
    propertyVisitId,
    jobId: property.jobId,
    name: property.name,
  };
}

function routeEndpoint(
  endpoint: RouteBuilderSession["inputs"]["startLocation"],
  fallbackName: string,
): RouteEndpointLocation {
  if (!endpoint.location) {
    return { ...endpoint };
  }
  return {
    _id: endpoint._id || createSessionId("route-endpoint"),
    name: endpoint.name || fallbackName,
    address: endpoint.address || "",
    location: endpoint.location ?? { lat: 0, lng: 0 },
  };
}

export function addRoute(
  session: RouteBuilderSession,
  sourceRouteId?: string | null,
): RouteBuilderSession {
  const prefix =
    session.origin === "blank"
      ? "Untitled Route"
      : suggestRouteNamePrefix(session.inputs);
  const usedNames = new Set(session.routes.map((route) => route.name));
  let number = 1;
  while (usedNames.has(`${prefix} ${number}`)) number += 1;
  const endpoints =
    session.routes.find((route) => route.id === sourceRouteId) ??
    session.inputs;
  const route: ProposedRoute = {
    id: createSessionId("route-builder-route"),
    name: `${prefix} ${number}`,
    color: leastUsedRouteColor(session.routes.map((route) => route.color)),
    startLocation: routeEndpoint(endpoints.startLocation, "Start"),
    endLocation: routeEndpoint(endpoints.endLocation, "End"),
    stops: [],
    instructions: "",
    metrics: {
      drivingDurationSeconds: 0,
      serviceDurationSeconds: 0,
      totalDurationSeconds: 0,
      drivingDistanceMeters: 0,
      drivingPath: "",
    },
  };
  const routes = [...session.routes, route];
  return {
    ...session,
    status: "adjusted",
    inputs: { ...session.inputs, routeCount: routes.length },
    routes,
  };
}

export function setRouteEndpoint(
  session: RouteBuilderSession,
  routeId: string,
  type: "start" | "end",
  location: RouteEndpointLocation,
): RouteBuilderSession {
  const route = session.routes.find((candidate) => candidate.id === routeId);
  if (!route) return session;

  const routeVisits = visitsForRoute(session, routeId);
  const routes = session.routes.map((candidate) => {
    if (candidate.id !== routeId) return candidate;
    const startLocation =
      type === "start" ? { ...location } : candidate.startLocation;
    const endLocation =
      type === "end" ? { ...location } : candidate.endLocation;
    return {
      ...candidate,
      startLocation,
      endLocation,
      metrics: rebuildMetrics(
        routeVisits.map((visit) => visit.property),
        startLocation.location,
        endLocation.location,
        candidate.metrics.drivingPath,
        incorporatesTime(session.inputs),
      ),
    };
  });

  return {
    ...session,
    status: "adjusted",
    routes,
  };
}

export function removeRoute(
  session: RouteBuilderSession,
  routeId: string,
): RouteBuilderSession {
  const removed = session.routes.find((route) => route.id === routeId);
  if (!removed) return session;
  const routes = session.routes.filter((route) => route.id !== routeId);
  return {
    ...session,
    status: "adjusted",
    inputs: { ...session.inputs, routeCount: routes.length },
    routes,
    unroutedJobs: [
      ...session.unroutedJobs,
      ...removed.stops.map((stop) => ({
        propertyVisitId: stop.propertyVisitId,
        jobId: stop.propertyId,
        name:
          session.availableProperties.find(
            (property) => property.jobId === stop.propertyId,
          )?.name ?? "Unknown property",
      })),
    ],
  };
}

export function renameRoute(
  session: RouteBuilderSession,
  routeId: string,
  name: string,
): RouteBuilderSession {
  const trimmed = name.trim();
  if (!trimmed) return session;
  return {
    ...session,
    status: "adjusted",
    routes: session.routes.map((route) =>
      route.id === routeId ? { ...route, name: trimmed } : route,
    ),
  };
}

export function setRouteInstructions(
  session: RouteBuilderSession,
  routeId: string,
  instructions: string,
): RouteBuilderSession {
  return {
    ...session,
    status: "adjusted",
    routes: session.routes.map((route) =>
      route.id === routeId ? { ...route, instructions } : route,
    ),
  };
}

export function setRouteColor(
  session: RouteBuilderSession,
  routeId: string,
  color: RouteColorName,
): RouteBuilderSession {
  const current = session.routes.find((route) => route.id === routeId);
  if (!current || current.color === color) return session;
  return {
    ...session,
    status: "adjusted",
    routes: session.routes.map((route) =>
      route.id === routeId ? { ...route, color } : route,
    ),
  };
}

/** Move a route within session.routes (list-view column order). */
export function reorderRoutes(
  session: RouteBuilderSession,
  routeId: string,
  toIndex: number,
): RouteBuilderSession {
  const fromIndex = session.routes.findIndex((route) => route.id === routeId);
  if (fromIndex < 0) return session;
  const clamped = Math.max(0, Math.min(toIndex, session.routes.length - 1));
  if (fromIndex === clamped) return session;
  const routes = [...session.routes];
  const [moved] = routes.splice(fromIndex, 1);
  routes.splice(clamped, 0, moved);
  return {
    ...session,
    status: "adjusted",
    routes,
  };
}
